/**
 * 「気球を探す」（docs/08 §234）。クルーが、遠くの・逆光の気球の中から自分のチームの気球を見分けるための画面。
 *
 * - 画面の上の約3分の1にカメラの映像を出し、受信している気球の「いま居るはずの位置」に印を重ねる
 *   （自分のGPS位置・気球の位置と高度から方位・仰角を計算し、端末の向きと傾きで映像の上に置く）
 * - その下に、方位（真方位）のテープを横に流す。テープの上にも気球の方位の印
 * - 既定は★のお気に入りの気球だけ（全機に切り替えられる）
 * - 気球が映像の真ん中（±3°）に来たら振動で知らせる
 * - **補正**: コンパスは5〜15°ずれる（車のそばではもっと）。映像の中の気球をタップして「これが○○」と合わせると、
 *   方位・仰角のずれを覚えて全部の印を合わせる。映像の画角（機種で違う）も調整できる
 *
 * 利用者と決めたこと: クルーモードだけ、スマホの縦持ちだけ、既定は★（選べる）、振動あり、補正あり。
 */

import { requestCompassPermission } from '../live/compass.ts';
import { watchOwnPosition } from '../live/geolocation.ts';
import {
  angleDiff,
  cameraAzEl,
  focalPx,
  lookAngles,
  normDeg,
  predictPosition,
  projectToScreen,
  rayFromScreen,
  rotationMatrix,
  smoothAngle,
  type CameraView,
  type FixLike,
  type Mat3,
  type Orientation,
} from './geometry.ts';

export interface FinderDeps {
  terminals: () => ReadonlyArray<{ terminalId: string; fixes: readonly FixLike[]; latest: FixLike }>;
  favorites: () => ReadonlySet<string>;
  colorFor: (terminalId: string) => string;
  /** 自分の端末ID（自分の送信を印にしない） */
  ownTerminalId: () => string;
  /** アプリが持っている自分のGPSの位置（送信中など）。無ければ画面が自分で取る */
  selfFix: () => { t: number; lat: number; lon: number; altMsl: number | null } | null;
  /** 磁北と真北の差（東偏が正） */
  declination: (lat: number, lon: number) => number;
  setStatus: (message: string, kind?: 'info' | 'warn') => void;
}

const CALIB_KEY = 'gv4d-finder-calibration';
const MODE_KEY = 'gv4d-finder-targets';
/** 真ん中に来たとみなす角度・知らせ直すまでに離れる角度 */
const CENTER_DEG = 3;
const REARM_DEG = 6;
const FEET_PER_M = 3.28084;

interface Calibration {
  /** 方位のずれ [°]（真方位 = 端末の方位 + 基準 + これ） */
  headingDeg: number;
  /** 仰角のずれ [°] */
  elevationDeg: number;
  /** 映像の長辺方向の画角 [°] */
  fovDeg: number;
}

function loadCalibration(): Calibration {
  try {
    const c = JSON.parse(localStorage.getItem(CALIB_KEY) ?? 'null') as Partial<Calibration> | null;
    return { headingDeg: c?.headingDeg ?? 0, elevationDeg: c?.elevationDeg ?? 0, fovDeg: c?.fovDeg ?? 65 };
  } catch {
    return { headingDeg: 0, elevationDeg: 0, fovDeg: 65 };
  }
}
function saveCalibration(c: Calibration): void {
  try {
    localStorage.setItem(CALIB_KEY, JSON.stringify(c));
  } catch {
    // 保存できなくても今回は効く
  }
}

/**
 * 振動で知らせる。Android は Vibration API。iPhone の Safari には無いので、iOS 18 からの
 * 「スイッチ（`<input type="checkbox" switch>`）を切り替えると端末が軽く震える」性質を使う（効かない版もある）
 */
let hapticLabel: HTMLLabelElement | null = null;
export function haptic(): void {
  if (typeof navigator.vibrate === 'function') {
    navigator.vibrate(60);
    return;
  }
  if (!hapticLabel) {
    hapticLabel = document.createElement('label');
    hapticLabel.setAttribute('aria-hidden', 'true');
    hapticLabel.style.cssText = 'position:fixed;left:-9999px;top:0;opacity:0;pointer-events:none';
    const input = document.createElement('input');
    input.type = 'checkbox';
    input.setAttribute('switch', '');
    hapticLabel.appendChild(input);
    document.body.appendChild(hapticLabel);
  }
  hapticLabel.click();
}

interface Target {
  id: string;
  color: string;
  favorite: boolean;
  trueAz: number;
  el: number;
  distM: number;
  altM: number | null;
  ageMs: number;
  /** 位置の不確かさ [m]（古いほど大きい） */
  errM: number;
}

export function setupBalloonFinder(deps: FinderDeps): { open: () => Promise<void>; close: () => void; isOpen: () => boolean } {
  const root = document.getElementById('balloon-finder') as HTMLElement;
  const video = document.getElementById('finder-video') as HTMLVideoElement;
  const overlay = document.getElementById('finder-overlay') as HTMLCanvasElement;
  const tape = document.getElementById('finder-tape') as HTMLCanvasElement;
  const msg = document.getElementById('finder-msg') as HTMLElement;
  const modeBtn = document.getElementById('finder-mode') as HTMLButtonElement;
  const calibBtn = document.getElementById('finder-calib-toggle') as HTMLButtonElement;
  const calibBox = document.getElementById('finder-calib') as HTMLElement;
  const calibTarget = document.getElementById('finder-calib-target') as HTMLSelectElement;
  const calibNote = document.getElementById('finder-calib-note') as HTMLElement;
  const fovInput = document.getElementById('finder-fov') as HTMLInputElement;
  const fovValue = document.getElementById('finder-fov-value') as HTMLElement;

  let stream: MediaStream | null = null;
  let raf = 0;
  let orientation: Orientation | null = null;
  let compassKind: 'absolute' | 'ios' | 'relative' = 'relative';
  /** iPhone: 端末の方位（alpha の基準）と磁方位の差（webkitCompassHeading から、ならして求める） */
  let iosOffset: number | null = null;
  let ownFix: { t: number; lat: number; lon: number; altMsl: number | null } | null = null;
  let stopGeo: (() => void) | null = null;
  let calib = loadCalibration();
  let showAll = (() => {
    try {
      return localStorage.getItem(MODE_KEY) === 'all';
    } catch {
      return false;
    }
  })();
  const armed = new Map<string, boolean>();
  let lastTargets: Target[] = [];
  let lastView: { r: Mat3; cam: CameraView; baseH: number } | null = null;

  const onOrientation = (e: Event): void => {
    const ev = e as DeviceOrientationEvent & { webkitCompassHeading?: number };
    if (ev.alpha == null || ev.beta == null || ev.gamma == null) return;
    // Android: 磁北が基準の absolute が来るなら、そちらだけを使う
    if (e.type === 'deviceorientation' && compassKind === 'absolute') return;
    orientation = { alpha: ev.alpha, beta: ev.beta, gamma: ev.gamma };
    if (e.type === 'deviceorientationabsolute' || ev.absolute === true) {
      compassKind = 'absolute';
    } else if (typeof ev.webkitCompassHeading === 'number' && ev.webkitCompassHeading >= 0) {
      compassKind = 'ios';
      // 縦に構えている間だけ（平らに置くと、カメラの向きが決まらない）
      if (ev.beta > 45 && ev.beta < 135) {
        iosOffset = smoothAngle(iosOffset, ev.webkitCompassHeading - cameraAzEl(orientation).az, 0.05);
      }
    }
  };

  /** 端末の方位 → 真方位 の基準（補正を除く） */
  const baseHeading = (self: { lat: number; lon: number }): number => {
    const decl = deps.declination(self.lat, self.lon);
    if (compassKind === 'absolute') return decl;
    if (compassKind === 'ios' && iosOffset != null) return iosOffset + decl;
    return 0;
  };

  const selfPosition = (): { lat: number; lon: number; altM: number } | null => {
    const a = deps.selfFix();
    const f = a && (!ownFix || a.t >= ownFix.t) ? a : ownFix;
    if (!f) return null;
    return { lat: f.lat, lon: f.lon, altM: f.altMsl ?? 0 };
  };

  const collectTargets = (self: { lat: number; lon: number; altM: number }, now: number): Target[] => {
    const favs = deps.favorites();
    const own = deps.ownTerminalId();
    const out: Target[] = [];
    for (const t of deps.terminals()) {
      if (t.terminalId === own) continue;
      const fav = favs.has(t.terminalId);
      if (!showAll && !fav) continue;
      const p = predictPosition(t.fixes.length ? t.fixes : [t.latest], now);
      if (!p) continue;
      // 30分より古い位置は出さない（とうに降りている）
      if (p.ageMs > 30 * 60_000) continue;
      const look = lookAngles(self, { lat: p.lat, lon: p.lon, altM: p.altM ?? self.altM });
      const errM = 30 + p.speedMps * Math.min(p.ageMs / 1000, 120) * 0.3 + Math.max(0, p.ageMs / 1000 - 120) * 3;
      out.push({
        id: t.terminalId,
        color: deps.colorFor(t.terminalId),
        favorite: fav,
        trueAz: look.azDeg,
        el: look.elDeg,
        distM: look.distM,
        altM: p.altM,
        ageMs: p.ageMs,
        errM,
      });
    }
    return out;
  };

  const setMsg = (text: string): void => {
    msg.textContent = text;
    msg.hidden = !text;
  };

  const sizeCanvas = (c: HTMLCanvasElement): CanvasRenderingContext2D => {
    const dpr = window.devicePixelRatio || 1;
    const w = c.clientWidth;
    const h = c.clientHeight;
    if (c.width !== Math.round(w * dpr) || c.height !== Math.round(h * dpr)) {
      c.width = Math.round(w * dpr);
      c.height = Math.round(h * dpr);
    }
    const ctx = c.getContext('2d')!;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, w, h);
    return ctx;
  };

  const label = (ctx: CanvasRenderingContext2D, text: string, x: number, y: number, align: CanvasTextAlign, color = '#fff'): void => {
    ctx.font = '600 12px system-ui, sans-serif';
    ctx.textAlign = align;
    ctx.textBaseline = 'middle';
    ctx.lineWidth = 3;
    ctx.strokeStyle = 'rgba(0,0,0,0.85)';
    ctx.strokeText(text, x, y);
    ctx.fillStyle = color;
    ctx.fillText(text, x, y);
  };

  const fmtDist = (m: number): string => (m >= 1000 ? `${(m / 1000).toFixed(1)}km` : `${Math.round(m)}m`);

  const draw = (): void => {
    raf = requestAnimationFrame(draw);
    const now = Date.now();
    const ctx = sizeCanvas(overlay);
    const tctx = sizeCanvas(tape);
    const W = overlay.clientWidth;
    const H = overlay.clientHeight;
    const self = selfPosition();
    if (!self) {
      setMsg('現在地を取得中…（位置情報の許可が要ります）');
      lastView = null;
      return;
    }
    if (!orientation) {
      setMsg('端末の向きを取得中…（向きの許可が要ります）');
      lastView = null;
      return;
    }
    const vw = video.videoWidth || W;
    const vh = video.videoHeight || H;
    const cam: CameraView = { width: W, height: H, focal: focalPx(W, H, vw, vh, calib.fovDeg) };
    const r = rotationMatrix(orientation);
    const baseH = baseHeading(self);
    const H0 = baseH + calib.headingDeg;
    lastView = { r, cam, baseH };
    const camLook = cameraAzEl(orientation);
    const camTrueAz = normDeg(camLook.az + H0);
    const targets = collectTargets(self, now);
    lastTargets = targets;

    const notes: string[] = [];
    if (compassKind === 'relative') notes.push('方位の基準がありません。「補正」で合わせてください');
    if (targets.length === 0) notes.push(showAll ? '受信中の気球がありません' : '★の気球がありません（「全機」に切り替えられます）');
    setMsg(notes.join(' ／ '));

    // --- 映像の上の印 ---
    const nearest: { id: string; d: number } = { id: '', d: Infinity };
    for (const t of targets) {
      const az = t.trueAz - H0;
      const el = t.el - calib.elevationDeg;
      const p = projectToScreen(r, az, el, cam);
      const dAz = angleDiff(t.trueAz, camTrueAz);
      if (Math.abs(dAz) < Math.abs(nearest.d)) {
        nearest.id = t.id;
        nearest.d = dAz;
      }
      const centered = p.front && Math.abs(dAz) <= CENTER_DEG;
      // 振動: 真ん中に入ったとき1回。離れたら知らせ直せるようにする
      if (centered && armed.get(t.id) !== false) {
        haptic();
        armed.set(t.id, false);
      } else if (Math.abs(dAz) > REARM_DEG) {
        armed.set(t.id, true);
      }
      const info = `${t.favorite ? '★' : ''}${t.id} ${fmtDist(t.distM)}${t.altM != null ? ` ${Math.round(t.altM * FEET_PER_M)}ft` : ''}`;
      if (p.front && p.x >= 0 && p.x <= W && p.y >= -20 && p.y <= H + 20) {
        const rad = Math.max(14, Math.min(W / 3, cam.focal * Math.tan(Math.atan2(t.errM, Math.max(t.distM, 1)))));
        const y = Math.min(Math.max(p.y, 0), H);
        ctx.lineWidth = centered ? 4 : 2.5;
        ctx.strokeStyle = 'rgba(0,0,0,0.6)';
        ctx.beginPath();
        ctx.arc(p.x, y, rad + 1.5, 0, Math.PI * 2);
        ctx.stroke();
        ctx.strokeStyle = t.color;
        ctx.beginPath();
        ctx.arc(p.x, y, rad, 0, Math.PI * 2);
        ctx.stroke();
        label(ctx, info, p.x, Math.max(10, y - rad - 9), 'center', centered ? '#ffef5a' : '#fff');
        if (t.ageMs > 90_000) label(ctx, `${Math.round(t.ageMs / 60_000)}分前の位置`, p.x, Math.min(H - 8, y + rad + 9), 'center', '#ffb4a8');
      } else {
        // 画面の外: 左右の端に、回す向きと角度
        const left = dAz < 0;
        const y = Math.min(Math.max(p.front ? p.y : H / 2, 16), H - 16);
        const x = left ? 6 : W - 6;
        ctx.fillStyle = t.color;
        ctx.beginPath();
        ctx.moveTo(x, y);
        ctx.lineTo(left ? x + 12 : x - 12, y - 8);
        ctx.lineTo(left ? x + 12 : x - 12, y + 8);
        ctx.closePath();
        ctx.fill();
        label(ctx, `${info} ${Math.abs(Math.round(dAz))}°`, left ? x + 16 : x - 16, y, left ? 'left' : 'right');
      }
    }
    // 真ん中の照準
    ctx.strokeStyle = 'rgba(255,255,255,0.7)';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(W / 2 - 10, H / 2);
    ctx.lineTo(W / 2 + 10, H / 2);
    ctx.moveTo(W / 2, H / 2 - 10);
    ctx.lineTo(W / 2, H / 2 + 10);
    ctx.stroke();

    // --- 方位のテープ（真方位） ---
    const TW = tape.clientWidth;
    const TH = tape.clientHeight;
    tctx.fillStyle = 'rgba(8,14,22,0.92)';
    tctx.fillRect(0, 0, TW, TH);
    const pxPerDeg = (cam.focal * Math.PI) / 180;
    const span = TW / 2 / pxPerDeg;
    const names: Record<number, string> = { 0: 'N', 45: 'NE', 90: 'E', 135: 'SE', 180: 'S', 225: 'SW', 270: 'W', 315: 'NW' };
    for (let d = Math.ceil((camTrueAz - span) / 5) * 5; d <= camTrueAz + span; d += 5) {
      const x = TW / 2 + angleDiff(d, camTrueAz) * pxPerDeg;
      const n = normDeg(d);
      const major = n % 10 === 0;
      tctx.strokeStyle = 'rgba(255,255,255,0.55)';
      tctx.lineWidth = 1;
      tctx.beginPath();
      tctx.moveTo(x, 0);
      tctx.lineTo(x, major ? 9 : 5);
      tctx.stroke();
      if (major) {
        tctx.font = names[n] ? '700 12px system-ui, sans-serif' : '11px system-ui, sans-serif';
        tctx.fillStyle = names[n] ? '#ffd166' : 'rgba(255,255,255,0.8)';
        tctx.textAlign = 'center';
        tctx.textBaseline = 'top';
        tctx.fillText(names[n] ?? String(n).padStart(3, '0'), x, 11);
      }
    }
    for (const t of targets) {
      const dAz = angleDiff(t.trueAz, camTrueAz);
      const x = Math.min(Math.max(TW / 2 + dAz * pxPerDeg, 6), TW - 6);
      tctx.fillStyle = t.color;
      tctx.beginPath();
      tctx.moveTo(x, TH - 13);
      tctx.lineTo(x - 6, TH - 2);
      tctx.lineTo(x + 6, TH - 2);
      tctx.closePath();
      tctx.fill();
    }
    // 真ん中の線
    tctx.strokeStyle = '#ff3b30';
    tctx.lineWidth = 2;
    tctx.beginPath();
    tctx.moveTo(TW / 2, 0);
    tctx.lineTo(TW / 2, TH);
    tctx.stroke();

    // いまの方位（真方位）・仰角は、映像の下の真ん中に大きく
    const elNow = camLook.el + calib.elevationDeg;
    const headText = `${String(Math.round(camTrueAz) % 360).padStart(3, '0')}°`;
    const elText = `仰角 ${elNow >= 0 ? '+' : ''}${Math.round(elNow)}°`;
    ctx.fillStyle = 'rgba(8,14,22,0.72)';
    ctx.fillRect(W / 2 - 62, H - 30, 124, 26);
    ctx.font = '700 17px system-ui, sans-serif';
    ctx.textAlign = 'right';
    ctx.textBaseline = 'middle';
    ctx.fillStyle = '#fff';
    ctx.fillText(headText, W / 2 + 2, H - 17);
    ctx.font = '12px system-ui, sans-serif';
    ctx.textAlign = 'left';
    ctx.fillStyle = 'rgba(255,255,255,0.85)';
    ctx.fillText(elText, W / 2 + 8, H - 17);
  };

  const renderMode = (): void => {
    modeBtn.textContent = showAll ? '全機' : '★だけ';
    modeBtn.setAttribute('aria-pressed', String(!showAll));
  };
  modeBtn.onclick = () => {
    showAll = !showAll;
    try {
      localStorage.setItem(MODE_KEY, showAll ? 'all' : 'favorites');
    } catch {
      // 保存できなくても今回は効く
    }
    renderMode();
  };

  // --- 補正 ---
  const renderCalib = (): void => {
    fovInput.value = String(calib.fovDeg);
    fovValue.textContent = `${calib.fovDeg}°`;
    const s = (v: number) => `${v >= 0 ? '+' : ''}${v.toFixed(1)}°`;
    calibNote.textContent = `いまの補正: 方位 ${s(calib.headingDeg)}・仰角 ${s(calib.elevationDeg)}`;
  };
  const fillCalibTargets = (): void => {
    const keep = calibTarget.value;
    calibTarget.innerHTML = lastTargets
      .map((t) => `<option value="${t.id.replace(/"/g, '&quot;')}">${t.favorite ? '★' : ''}${t.id}（${fmtDist(t.distM)}）</option>`)
      .join('');
    if (keep && lastTargets.some((t) => t.id === keep)) calibTarget.value = keep;
  };
  calibBtn.onclick = () => {
    const show = calibBox.hidden;
    calibBox.hidden = !show;
    calibBtn.setAttribute('aria-pressed', String(show));
    if (show) {
      fillCalibTargets();
      renderCalib();
    }
  };
  fovInput.oninput = () => {
    calib = { ...calib, fovDeg: Number(fovInput.value) };
    saveCalibration(calib);
    renderCalib();
  };
  (document.getElementById('finder-calib-reset') as HTMLButtonElement).onclick = () => {
    calib = { headingDeg: 0, elevationDeg: 0, fovDeg: 65 };
    saveCalibration(calib);
    renderCalib();
    deps.setStatus('気球を探す: 補正を消しました');
  };
  (document.getElementById('finder-calib-done') as HTMLButtonElement).onclick = () => {
    calibBox.hidden = true;
    calibBtn.setAttribute('aria-pressed', 'false');
  };
  // 補正の画面を開いている間、映像をタップ = 選んだ気球の本当の位置
  overlay.addEventListener('click', (e) => {
    if (calibBox.hidden || !lastView || !orientation) return;
    const t = lastTargets.find((x) => x.id === calibTarget.value);
    if (!t) {
      deps.setStatus('補正: 合わせる気球を選んでください', 'warn');
      return;
    }
    const rect = overlay.getBoundingClientRect();
    const ray = rayFromScreen(lastView.r, e.clientX - rect.left, e.clientY - rect.top, lastView.cam);
    calib = {
      ...calib,
      headingDeg: Math.round(angleDiff(t.trueAz, ray.az + lastView.baseH) * 10) / 10,
      elevationDeg: Math.round((t.el - ray.el) * 10) / 10,
    };
    saveCalibration(calib);
    renderCalib();
    haptic();
    deps.setStatus(`気球を探す: ${t.id} に合わせました（方位 ${calib.headingDeg}°・仰角 ${calib.elevationDeg}°）`);
  });
  setInterval(() => {
    if (!calibBox.hidden) fillCalibTargets();
  }, 3000);

  const close = (): void => {
    root.hidden = true;
    document.body.classList.remove('finder-open');
    cancelAnimationFrame(raf);
    stream?.getTracks().forEach((t) => t.stop());
    stream = null;
    video.srcObject = null;
    window.removeEventListener('deviceorientation', onOrientation);
    window.removeEventListener('deviceorientationabsolute', onOrientation);
    stopGeo?.();
    stopGeo = null;
    orientation = null;
  };

  /** 開く。★ ボタンを押した処理の中で呼ぶこと（iPhone の向きの許可・カメラの許可はその中でしか出せない） */
  const open = async (): Promise<void> => {
    // 許可を求めるのは、await を挟む前に（iPhone はユーザー操作の直後でないと出さない）
    const permP = requestCompassPermission();
    const camP = navigator.mediaDevices?.getUserMedia
      ? navigator.mediaDevices.getUserMedia({ video: { facingMode: { ideal: 'environment' } }, audio: false })
      : Promise.reject(new Error('この端末・ブラウザはカメラに対応していません'));
    root.hidden = false;
    document.body.classList.add('finder-open');
    renderMode();
    setMsg('カメラと向きのセンサーを準備しています…');
    window.addEventListener('deviceorientationabsolute', onOrientation);
    window.addEventListener('deviceorientation', onOrientation);
    stopGeo = watchOwnPosition(
      (f) => {
        ownFix = { t: f.t, lat: f.lat, lon: f.lon, altMsl: f.altMsl };
      },
      (m) => deps.setStatus(`気球を探す: 位置情報 ${m}`, 'warn'),
    );
    if ((await permP) !== 'granted') deps.setStatus('気球を探す: 向きのセンサーが許可されていません', 'warn');
    try {
      stream = await camP;
      video.srcObject = stream;
      await video.play().catch(() => {});
    } catch (e) {
      deps.setStatus(`気球を探す: カメラを使えません（${(e as Error).message}）。方位のテープだけ出します`, 'warn');
    }
    cancelAnimationFrame(raf);
    raf = requestAnimationFrame(draw);
  };

  (document.getElementById('finder-close') as HTMLButtonElement).onclick = close;
  return { open, close, isOpen: () => !root.hidden };
}
